import { gatewayExternalLinks, loraDeviceExternalLinks } from "../../shared.js";
import { dayMs, hourMs, minuteMs } from "../../timeConstants.js";
import { AssetType } from "../AssetTypeConfiguration/AssetTypeConfiguration.js";
import {
    saplingBatteryLabelConversion,
    saplingBatteryWarningConversion,
    sproutBatteryLabelConversion,
    sproutBatteryWarningConversion,
} from "../DeviceConfiguration/deviceBatteryHelpers.js";
import {
    decommissionedDeviceRegistration,
    gen4BlockQRCodeValidation,
    type QRValidationFunction,
    sproutBlockQRCodeValidation,
    sproutMicroQRCodeValidation,
} from "../DeviceConfiguration/deviceQrCodeHelpers.js";
import { portDefinitionCatalogue } from "../portDefinitionCatalogue.js";
import { type DeviceTypeConfigurationTable, DeviceTypeSupportStatus } from "./types.js";

// RAK Gateway QR code format: LW:D0:FFFFFFFFFFFFFFFF:<EUI>:FFFF4201
const rakWirelessRakGatewayQRCodeValidation: QRValidationFunction = (QR: string) => {
    const match = QR.match(/^LW:D0:FFFFFFFFFFFFFFFF:([0-9a-fA-F]{16}):FFFF42[0-9a-fA-F]{2}$/i);
    if (!match || !match[1]) {
        return false;
    }
    return "eui-" + match[1].toLowerCase();
};

// Sensoterra probe QR codes encode the numeric node_id (serial number) directly, e.g. 18000294799
export const SENSOTERRA_PROBE_QR_FORMAT = /^(?<enclosureId>\d{11})$/;

// Dragino device QR codes encode the EUI directly as a 16-char hex string, e.g. a84041ffff287bd4
export const DRAGINO_DEVICE_QR_FORMAT = /^(?<eui>[0-9a-fA-F]{16})$/;

// ePlant TreeTag QR codes encode a URL with the human-readable device name, e.g.
//   https://www.eplant.bio/sn1/?A6075
export const EPLANT_TREE_TAG_QR_FORMAT = /^https:\/\/www\.eplant\.bio\/sn1\/\?(?<enclosureId>[A-Z]+[0-9]+)$/;

// Key order is load-bearing: it feeds the generated OpenAPI device-type enums.
export const DeviceTypeConfigurations: DeviceTypeConfigurationTable = {
    sap_1v: {
        type: "sap_1v",
        assetType: AssetType.DEVICE,
        preferredVerdiIdStart: "B",
        isVerdiDevice: true,
        manufacturerName: "Verdi",
        friendlyName: "Single-Valve Micro-Block Controller (Sapling)",
        friendlyNameShort: "Sap. 1V",
        generation: "sap",
        valveCount: 1,
        valveType: "micro",
        supportedSensors: 2,
        deviceTypeSupportStatus: DeviceTypeSupportStatus.RETIRED,
        validateQRCode: decommissionedDeviceRegistration,
        iconName: "single_micro",
        descriptionText: "For series connections",
        batteryIndicatorToSpecificLabel: saplingBatteryLabelConversion,
        batteryIndicatorToWarningLevel: saplingBatteryWarningConversion,
        externalLinksConfigInfo: loraDeviceExternalLinks,
        fallbackOfflineThresholdMs: 2 * hourMs,
        isGateway: false,
        isStandaloneSingleSensor: false,
        sensorPortConfiguration: {
            S1: { portId: "S1", ...portDefinitionCatalogue.seedSap_S },
            S2: { portId: "S2", ...portDefinitionCatalogue.seedSap_S },
            I1: { portId: "I1", ...portDefinitionCatalogue.seedSap_I_tempHum },
            D1: { portId: "D1", ...portDefinitionCatalogue.D_crashCount },
            D2: { portId: "D2", ...portDefinitionCatalogue.D_resetCount },
            D3: { portId: "D3", ...portDefinitionCatalogue.seedSap_D_networkStats },
            D4: { portId: "D4", ...portDefinitionCatalogue.seedSap_D_battery },
            D5: { portId: "D5", ...portDefinitionCatalogue.seedSap_D_saplingVerification },
            O1: { portId: "O1", ...portDefinitionCatalogue.O_output },
        },
    },
    sap_2v: {
        type: "sap_2v",
        assetType: AssetType.DEVICE,
        preferredVerdiIdStart: "B",
        isVerdiDevice: true,
        manufacturerName: "Verdi",
        friendlyName: "Double-Valve Micro-Block Controller (Sapling)",
        friendlyNameShort: "Sap. 2V",
        generation: "sap",
        valveCount: 2,
        valveType: "micro",
        supportedSensors: 2,
        deviceTypeSupportStatus: DeviceTypeSupportStatus.RETIRED,
        validateQRCode: decommissionedDeviceRegistration,
        iconName: "double_micro",
        descriptionText: "For parallel connections",
        batteryIndicatorToSpecificLabel: saplingBatteryLabelConversion,
        batteryIndicatorToWarningLevel: saplingBatteryWarningConversion,
        externalLinksConfigInfo: loraDeviceExternalLinks,
        fallbackOfflineThresholdMs: 2 * hourMs,
        isGateway: false,
        isStandaloneSingleSensor: false,
        sensorPortConfiguration: {
            S1: { portId: "S1", ...portDefinitionCatalogue.seedSap_S },
            S2: { portId: "S2", ...portDefinitionCatalogue.seedSap_S },
            I1: { portId: "I1", ...portDefinitionCatalogue.seedSap_I_tempHum },
            D1: { portId: "D1", ...portDefinitionCatalogue.D_crashCount },
            D2: { portId: "D2", ...portDefinitionCatalogue.D_resetCount },
            D3: { portId: "D3", ...portDefinitionCatalogue.seedSap_D_networkStats },
            D4: { portId: "D4", ...portDefinitionCatalogue.seedSap_D_battery },
            D5: { portId: "D5", ...portDefinitionCatalogue.seedSap_D_saplingVerification },
            O1: { portId: "O1", ...portDefinitionCatalogue.O_output },
            O2: { portId: "O2", ...portDefinitionCatalogue.O_output },
        },
    },
    sap_sense: {
        type: "sap_sense",
        assetType: AssetType.DEVICE,
        preferredVerdiIdStart: "B",
        isVerdiDevice: true,
        manufacturerName: "Verdi",
        friendlyName: "Sensor-Only Micro-Block Controller (Sapling)",
        friendlyNameShort: "Sap. Sense",
        generation: "sap",
        valveCount: 0,
        valveType: "none",
        supportedSensors: 2,
        deviceTypeSupportStatus: DeviceTypeSupportStatus.RETIRED,
        validateQRCode: decommissionedDeviceRegistration,
        iconName: "single_micro",
        descriptionText: "Just collecting data",
        batteryIndicatorToSpecificLabel: saplingBatteryLabelConversion,
        batteryIndicatorToWarningLevel: saplingBatteryWarningConversion,
        externalLinksConfigInfo: loraDeviceExternalLinks,
        fallbackOfflineThresholdMs: 2 * hourMs,
        isGateway: false,
        isStandaloneSingleSensor: false,
        sensorPortConfiguration: {
            S1: { portId: "S1", ...portDefinitionCatalogue.seedSap_S },
            S2: { portId: "S2", ...portDefinitionCatalogue.seedSap_S },
            I1: { portId: "I1", ...portDefinitionCatalogue.seedSap_I_tempHum },
            D1: { portId: "D1", ...portDefinitionCatalogue.D_crashCount },
            D2: { portId: "D2", ...portDefinitionCatalogue.D_resetCount },
            D3: { portId: "D3", ...portDefinitionCatalogue.seedSap_D_networkStats },
            D4: { portId: "D4", ...portDefinitionCatalogue.seedSap_D_battery },
        },
    },
    spr_1v: {
        type: "spr_1v",
        assetType: AssetType.DEVICE,
        preferredVerdiIdStart: "C",
        isVerdiDevice: true,
        manufacturerName: "Verdi",
        generation: "sprout",
        friendlyName: "Single-Valve Micro-Block Controller (Sprout)",
        friendlyNameShort: "Sprout 1V",
        valveCount: 1,
        valveType: "micro",
        supportedSensors: 2,
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ACTIVE,
        validateQRCode: sproutMicroQRCodeValidation,
        iconName: "single_micro",
        descriptionText: "For series connections",
        batteryIndicatorToSpecificLabel: sproutBatteryLabelConversion,
        batteryIndicatorToWarningLevel: sproutBatteryWarningConversion,
        externalLinksConfigInfo: loraDeviceExternalLinks,
        fallbackOfflineThresholdMs: 2 * hourMs,
        isGateway: false,
        isStandaloneSingleSensor: false,
        sensorPortConfiguration: {
            S1: { portId: "S1", ...portDefinitionCatalogue.sprout_S },
            S2: { portId: "S2", ...portDefinitionCatalogue.sprout_S },
            I1: { portId: "I1", ...portDefinitionCatalogue.sprout_I_tempHum },
            D1: { portId: "D1", ...portDefinitionCatalogue.D_crashCount },
            D2: { portId: "D2", ...portDefinitionCatalogue.D_resetCount },
            D3: { portId: "D3", ...portDefinitionCatalogue.sprout_D_networkStats },
            D4: { portId: "D4", ...portDefinitionCatalogue.sprout_D_battery },
            O1: { portId: "O1", ...portDefinitionCatalogue.O_output },
        },
    },
    spr_2v: {
        type: "spr_2v",
        assetType: AssetType.DEVICE,
        preferredVerdiIdStart: "C",
        isVerdiDevice: true,
        manufacturerName: "Verdi",
        friendlyName: "Double-Valve Micro-Block Controller (Sprout)",
        friendlyNameShort: "Sprout 2V",
        generation: "sprout",
        valveCount: 2,
        valveType: "micro",
        supportedSensors: 2,
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ACTIVE,
        validateQRCode: sproutMicroQRCodeValidation,
        iconName: "double_micro",
        descriptionText: "For parallel connections",
        batteryIndicatorToSpecificLabel: sproutBatteryLabelConversion,
        batteryIndicatorToWarningLevel: sproutBatteryWarningConversion,
        externalLinksConfigInfo: loraDeviceExternalLinks,
        fallbackOfflineThresholdMs: 2 * hourMs,
        isGateway: false,
        isStandaloneSingleSensor: false,
        sensorPortConfiguration: {
            S1: { portId: "S1", ...portDefinitionCatalogue.sprout_S },
            S2: { portId: "S2", ...portDefinitionCatalogue.sprout_S },
            I1: { portId: "I1", ...portDefinitionCatalogue.sprout_I_tempHum },
            D1: { portId: "D1", ...portDefinitionCatalogue.D_crashCount },
            D2: { portId: "D2", ...portDefinitionCatalogue.D_resetCount },
            D3: { portId: "D3", ...portDefinitionCatalogue.sprout_D_networkStats },
            D4: { portId: "D4", ...portDefinitionCatalogue.sprout_D_battery },
            O1: { portId: "O1", ...portDefinitionCatalogue.O_output },
            O2: { portId: "O2", ...portDefinitionCatalogue.O_output },
        },
    },
    spr_block: {
        type: "spr_block",
        assetType: AssetType.DEVICE,
        preferredVerdiIdStart: "C",
        isVerdiDevice: true,
        manufacturerName: "Verdi",
        friendlyName: "Block Controller (Sprout)",
        friendlyNameShort: "Sprout Block",
        generation: "sprout",
        valveCount: 1,
        valveType: "block",
        supportedSensors: 2,
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ADMIN_ONLY,
        validateQRCode: sproutBlockQRCodeValidation,
        iconName: "sproutBlock",
        descriptionText: "Connect to solenoid, relay, flow meter, transducer",
        batteryIndicatorToSpecificLabel: sproutBatteryLabelConversion,
        batteryIndicatorToWarningLevel: sproutBatteryWarningConversion,
        externalLinksConfigInfo: loraDeviceExternalLinks,
        fallbackOfflineThresholdMs: 2 * hourMs,
        isGateway: false,
        isStandaloneSingleSensor: false,
        sensorPortConfiguration: {
            S1: { portId: "S1", ...portDefinitionCatalogue.sprout_S },
            S2: { portId: "S2", ...portDefinitionCatalogue.sprout_S },
            I1: { portId: "I1", ...portDefinitionCatalogue.sprout_I_tempHum },
            D1: { portId: "D1", ...portDefinitionCatalogue.D_crashCount },
            D2: { portId: "D2", ...portDefinitionCatalogue.D_resetCount },
            D3: { portId: "D3", ...portDefinitionCatalogue.sprout_D_networkStats },
            D4: { portId: "D4", ...portDefinitionCatalogue.sprout_D_battery },
            O1: { portId: "O1", ...portDefinitionCatalogue.O_output },
        },
    },
    spr_sense: {
        type: "spr_sense",
        assetType: AssetType.DEVICE,
        preferredVerdiIdStart: "C",
        isVerdiDevice: true,
        manufacturerName: "Verdi",
        friendlyName: "Sensor-Only Micro-Block Controller (Sprout)",
        friendlyNameShort: "Sprout Sense",
        generation: "sprout",
        valveCount: 0,
        valveType: "none",
        supportedSensors: 2,
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ACTIVE,
        validateQRCode: sproutMicroQRCodeValidation,
        iconName: "single_micro",
        descriptionText: "Just collecting data",
        batteryIndicatorToSpecificLabel: sproutBatteryLabelConversion,
        batteryIndicatorToWarningLevel: sproutBatteryWarningConversion,
        externalLinksConfigInfo: loraDeviceExternalLinks,
        fallbackOfflineThresholdMs: 2 * hourMs,
        isGateway: false,
        isStandaloneSingleSensor: false,
        sensorPortConfiguration: {
            S1: { portId: "S1", ...portDefinitionCatalogue.sprout_S },
            S2: { portId: "S2", ...portDefinitionCatalogue.sprout_S },
            I1: { portId: "I1", ...portDefinitionCatalogue.sprout_I_tempHum },
            D1: { portId: "D1", ...portDefinitionCatalogue.D_crashCount },
            D2: { portId: "D2", ...portDefinitionCatalogue.D_resetCount },
            D3: { portId: "D3", ...portDefinitionCatalogue.sprout_D_networkStats },
            D4: { portId: "D4", ...portDefinitionCatalogue.sprout_D_battery },
        },
    },
    gen4_block: {
        type: "gen4_block",
        assetType: AssetType.DEVICE,
        preferredVerdiIdStart: "D",
        isVerdiDevice: true,
        manufacturerName: "Verdi",
        friendlyName: "Block Controller (Gen 4)",
        friendlyNameShort: "G4 Block",
        generation: "gen4",
        valveCount: 1,
        valveType: "block",
        supportedSensors: 3,
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ACTIVE,
        validateQRCode: gen4BlockQRCodeValidation,
        iconName: "sproutBlock",
        descriptionText: "Connect to solenoid, relay, flow meter, transducer",
        batteryIndicatorToSpecificLabel: sproutBatteryLabelConversion,
        batteryIndicatorToWarningLevel: sproutBatteryWarningConversion,
        externalLinksConfigInfo: loraDeviceExternalLinks,
        fallbackOfflineThresholdMs: 2 * hourMs,
        isGateway: false,
        isStandaloneSingleSensor: false,
        sensorPortConfiguration: {
            S1: { portId: "S1", ...portDefinitionCatalogue.gen4_S_adc },
            S2: { portId: "S2", ...portDefinitionCatalogue.gen4_S_pwm },
            S3: { portId: "S3", ...portDefinitionCatalogue.gen4_S_total },
            I1: { portId: "I1", ...portDefinitionCatalogue.gen4_I_tempHum },
            D1: { portId: "D1", ...portDefinitionCatalogue.D_crashCount },
            D2: { portId: "D2", ...portDefinitionCatalogue.D_resetCount },
            D3: { portId: "D3", ...portDefinitionCatalogue.gen4_D_lastErrorCode },
            D4: { portId: "D4", ...portDefinitionCatalogue.gen4_D_networkStats },
            D5: { portId: "D5", ...portDefinitionCatalogue.gen4_D_battery },
            O1: { portId: "O1", ...portDefinitionCatalogue.gen4_O_output },
        },
    },

    sensoterraProbe: {
        type: "sensoterraProbe",
        assetType: AssetType.THIRD_PARTY_DEVICE,
        identifierScheme: "loraEui",
        preferredVerdiIdStart: "R",
        isVerdiDevice: false,
        manufacturerName: "Sensoterra",
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ACTIVE,
        fallbackOfflineThresholdMs: dayMs,
        friendlyName: "Sensoterra Single-Depth Sensor",
        friendlyNameShort: "Sensoterra",
        defaultConnectedSensors: [],
        iconName: "msense",
        valveCount: 0,
        valveType: "none",
        supportedSensors: 0,
        validateQRCode: (QRData: string) => {
            const match = QRData.match(SENSOTERRA_PROBE_QR_FORMAT);
            if (!match) return false;
            return match.groups!.enclosureId;
        },

        descriptionText: "Measures Soil Moisture",
        batteryIndicatorToWarningLevel: (voltage) => {
            if (voltage === undefined || voltage < 1 || voltage > 11) {
                return "unknown";
            }
            if (voltage < 2.6) {
                return "critical";
            }
            if (voltage < 2.7) {
                return "low";
            }
            if (voltage < 2.8) {
                return "good";
            }
            return "full";
        },
        batteryIndicatorToSpecificLabel: (voltage) => {
            if (voltage === undefined || voltage < 1 || voltage > 11) {
                return "N/A";
            }
            return (Math.round(voltage * 100) / 100).toString() + "V";
        },
        externalLinksConfigInfo: loraDeviceExternalLinks,
        isStandaloneSingleSensor: true,
        isGateway: false,
        sensorPortConfiguration: {
            I1: { portId: "I1", ...portDefinitionCatalogue.sensoterra_S },
            D1: { portId: "D1", ...portDefinitionCatalogue.sensoterra_D_battery },
        },
    },

    draginoSDI12LBLSNode: {
        type: "draginoSDI12LBLSNode",
        assetType: AssetType.THIRD_PARTY_DEVICE,
        identifierScheme: "loraEui",
        preferredVerdiIdStart: "P",
        isVerdiDevice: false,
        manufacturerName: "Dragino",
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ACTIVE,
        fallbackOfflineThresholdMs: 40 * minuteMs,
        friendlyName: `SDI-12 Telemetry Node`,
        friendlyNameShort: "SDI-12 Node",
        defaultConnectedSensors: [],
        iconName: "draginoEC",
        valveCount: 0,
        valveType: "none",
        supportedSensors: 1,
        validateQRCode: (QRData: string) => {
            const match = QRData.match(DRAGINO_DEVICE_QR_FORMAT);
            if (!match) return false;
            return "eui-" + match.groups!.eui.toLowerCase();
        },
        descriptionText: "Connect to SDI-12 sensors",
        batteryIndicatorToWarningLevel: (_voltage) => "unknown",
        batteryIndicatorToSpecificLabel: (_voltage) => "unknown",
        externalLinksConfigInfo: loraDeviceExternalLinks,
        isStandaloneSingleSensor: true,
        isGateway: false,
        sensorPortConfiguration: {
            S1: { portId: "S1", ...portDefinitionCatalogue.draginoSDI12_S },
            D1: { portId: "D1", ...portDefinitionCatalogue.draginoSDI12_D_battery },
        },
    },

    draginoDL0S8N: {
        type: "draginoDL0S8N",
        assetType: AssetType.GATEWAY,
        identifierScheme: "loraEui",
        preferredVerdiIdStart: "D",
        isVerdiDevice: false,
        manufacturerName: "Dragino",
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ACTIVE,
        fallbackOfflineThresholdMs: 20 * minuteMs,
        friendlyName: "Gateway (Legacy)",
        friendlyNameShort: "Gateway (Legacy)",
        defaultConnectedSensors: [],
        iconName: "draginoDL0S8N",
        valveCount: 0,
        valveType: "none",
        supportedSensors: 0,
        validateQRCode: (QRData: string) => {
            const match = QRData.match(DRAGINO_DEVICE_QR_FORMAT);
            if (!match) return false;
            return "eui-" + match.groups!.eui.toLowerCase();
        },

        descriptionText: "Verdi Gateway (Legacy) — Dragino",
        batteryIndicatorToWarningLevel: (_voltage) => "unknown",
        batteryIndicatorToSpecificLabel: (_voltage) => "unknown",
        externalLinksConfigInfo: gatewayExternalLinks,
        isStandaloneSingleSensor: false,
        visibleRegardlessOfSelectedAOI: true,
        isGateway: true,
        actionToUpdateLastContact: "ttiGatewayConnectionStats",
    },

    rakWirelessRakGateway: {
        type: "rakWirelessRakGateway",
        assetType: AssetType.GATEWAY,
        identifierScheme: "loraEui",
        preferredVerdiIdStart: "G",
        isVerdiDevice: false,
        manufacturerName: "RAK Wireless",
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ACTIVE,
        fallbackOfflineThresholdMs: 20 * minuteMs,
        friendlyName: "Gateway (Gen 2)",
        friendlyNameShort: "Gateway (Gen 2)",
        defaultConnectedSensors: [],
        iconName: "rakWirelessRakGateway",
        valveCount: 0,
        valveType: "none",
        supportedSensors: 0,
        validateQRCode: rakWirelessRakGatewayQRCodeValidation,
        descriptionText: "Verdi Gateway (Gen 2) — RAK",
        batteryIndicatorToWarningLevel: (_voltage) => "unknown",
        batteryIndicatorToSpecificLabel: (_voltage) => "unknown",
        externalLinksConfigInfo: gatewayExternalLinks,
        isStandaloneSingleSensor: false,
        visibleRegardlessOfSelectedAOI: true,
        isGateway: true,
        actionToUpdateLastContact: "ttiGatewayConnectionStats",
    },

    ePlantTreeTag: {
        type: "ePlantTreeTag",
        assetType: AssetType.THIRD_PARTY_DEVICE,
        identifierScheme: "loraEui",
        preferredVerdiIdStart: "T",
        isVerdiDevice: false,
        manufacturerName: "ePlant",
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ACTIVE,
        fallbackOfflineThresholdMs: hourMs,
        friendlyName: "ePlant TreeTag",
        friendlyNameShort: "TreeTag",
        defaultConnectedSensors: [],
        iconName: "ePlantTreeTag",
        valveCount: 0,
        valveType: "none",
        supportedSensors: 0,
        validateQRCode: (QRData: string) => {
            const match = QRData.match(EPLANT_TREE_TAG_QR_FORMAT);
            if (!match) return false;
            return match.groups!.enclosureId;
        },
        descriptionText: "Tree circumference, lean, temperature, humidity",
        batteryIndicatorToWarningLevel: (voltage) => {
            if (voltage === undefined) return "unknown";
            if (voltage < 2.6) return "critical";
            if (voltage < 2.8) return "low";
            if (voltage < 3.2) return "good";
            return "full";
        },
        batteryIndicatorToSpecificLabel: (voltage) => {
            if (voltage === undefined) return "N/A";
            return `${voltage.toFixed(2)}V`;
        },
        externalLinksConfigInfo: loraDeviceExternalLinks,
        isStandaloneSingleSensor: true,
        isGateway: false,
        sensorPortConfiguration: {
            D1: { portId: "D1", ...portDefinitionCatalogue.ePlantTreeTag_D_battery },
        },
    },

    davisWeatherStation: {
        type: "davisWeatherStation",
        assetType: AssetType.THIRD_PARTY_DEVICE,
        // Identified by a WeatherLink station_id_uuid, not a LoRa EUI
        identifierScheme: "uuid",
        preferredVerdiIdStart: "W",
        isVerdiDevice: false,
        manufacturerName: "Davis Instruments",
        // Admin-only until the WeatherLink integration ships
        deviceTypeSupportStatus: DeviceTypeSupportStatus.ADMIN_ONLY,
        fallbackOfflineThresholdMs: hourMs,
        friendlyName: "Davis Weather Station",
        friendlyNameShort: "Weather Station",
        defaultConnectedSensors: [],
        iconName: "davisWeatherStation",
        valveCount: 0,
        valveType: "none",
        supportedSensors: 0,
        // Provisioned through the WeatherLink integration, never by scanning a label
        validateQRCode: (_QRData: string) => false,
        descriptionText: "Temperature, humidity, wind, and rainfall",
        batteryIndicatorToWarningLevel: (_voltage) => "unknown",
        batteryIndicatorToSpecificLabel: (_voltage) => "N/A",
        externalLinksConfigInfo: {},
        // Multi-sensor, so not the single-sensor case this flag names, despite needing no host node
        isStandaloneSingleSensor: false,
        visibleRegardlessOfSelectedAOI: true,
        isGateway: false,
        sensorPortConfiguration: {
            D1: { portId: "D1", ...portDefinitionCatalogue.davisWeatherLink_D_consoleBattery },
            D2: { portId: "D2", ...portDefinitionCatalogue.davisWeatherLink_D_transmitterBattery },
        },
    },
};

/** Shortest gateway offline threshold across all gateway types — safe to use when bumping a pending check. */
export const MIN_GATEWAY_OFFLINE_THRESHOLD_MS = Math.min(
    ...Object.values(DeviceTypeConfigurations)
        .filter((config) => config.isGateway)
        .map((config) => config.fallbackOfflineThresholdMs),
);
